import { isBrowserExtensionError } from "@/lib/rumExtensionFilter";
import { isDataEndpointView } from "@/lib/rumViewFilter";

import { datadogRum } from "@datadog/browser-rum";

window.currentTenant = window.currentTenant || {};
const env = window.currentTenant.environment;
const release = window.currentTenant.release;
const id = window.currentTenant.user_id;
const email = window.currentTenant.user_email;
const name = window.currentTenant.user_name;
const tenant = window.currentTenant.name;
const applicationId = window.currentTenant.dd_rum_application_id;
const clientToken = window.currentTenant.dd_rum_client_token;

if (applicationId && clientToken && !["development", "test"].includes(env)) {
  datadogRum.init({
    applicationId: applicationId,
    clientToken: clientToken,
    site: "datadoghq.com",
    service: "admin-web",
    env: env,
    version: release,
    sessionSampleRate: 50,
    sessionReplaySampleRate: 10,
    trackBfcacheViews: true,
    defaultPrivacyLevel: "mask-user-input",
    // Drop spurious views for `*/initial_page_data` JSON endpoints so they don't
    // pollute the per-view RUM metrics (ENG-7), and errors thrown wholly inside
    // a user's browser extension, which are not ours and swamp the real ones.
    beforeSend: (event) =>
      !isDataEndpointView(event) && !isBrowserExtensionError(event)
  });

  if (id) {
    datadogRum.setUser({
      id: id,
      name: name,
      email: email,
      tenant: tenant
    });
  }

  // Forward server-evaluated feature flags (Unleash via GitLab, injected into
  // window.currentTenant.feature_flags by _hidden_vars.html.erb) to RUM so views,
  // errors, and replays can be faceted by flag state (ENG-144). `flowbite` is a
  // hardcoded UI constant, not a real toggle, so it's excluded from the facet.
  const NON_FLAG_KEYS = ["flowbite"];
  const featureFlags = window.currentTenant.feature_flags || {};
  Object.entries(featureFlags).forEach(([key, value]) => {
    if (NON_FLAG_KEYS.includes(key)) return;
    datadogRum.addFeatureFlagEvaluation(key, value);
  });
}

// A named, queryable RUM action carrying structured context. Use for
// instrumenting a known flow (e.g. schedule navigation) so it can be faceted
// in RUM. No-op before init (dev/test), where addAction is buffered/dropped.
export function trackRumAction(name, context = {}) {
  datadogRum.addAction(name, context);
}

// An explicit, handled RUM error with context — lands in Error Tracking as its
// own issue (distinct from generic noise) and can be alerted on. No-op before
// init (dev/test).
export function reportRumError(message, context = {}) {
  datadogRum.addError(new Error(message), context);
}

// A bounded trace of where an error was actually thrown.
//
// The Error handed to addError above is manufactured here at the reporting
// site, so the stack it carries describes the reporter, not the throw. Callers
// that still hold the original error run it through this and pass the result as
// context, which is the only way the origin survives. ENG-591 left ~40 throws
// an hour that could not be placed for exactly this reason.
//
// Bundles are minified, so symbol names are not the point — the chunk name in
// the top frames is what separates our code from a vendored one. Frames are
// detected rather than sliced by position: V8 puts a message line first and
// Safari and Firefox do not, so a fixed offset drops a real frame on those.
// The message is already reported separately, so dropping it here also keeps
// whatever it contains out of a second field.
export function originStack(error) {
  if (!(error instanceof Error) || typeof error.stack !== "string") {
    return undefined;
  }

  const frames = error.stack
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("at ") || line.includes("@"));

  if (!frames.length) return undefined;

  return frames.slice(0, 5).join(" | ").slice(0, 500);
}
